import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import MarketplaceHeader from './components/MarketplaceHeader';
import { ToastProvider } from './components/ui/ToastContainer';
import { PageLoader } from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { getStorageItem } from './utils/storage';
import { isMarketplaceAuthenticated, isFarmer, isRestaurant } from './utils/users';
import { initializeMarketplaceData } from './data/marketplaceMockData';

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Forecasts = lazy(() => import('./pages/Forecasts'));
const ForecastDetail = lazy(() => import('./pages/ForecastDetail'));
const ForecastAlternative = lazy(() => import('./pages/ForecastAlternative'));
const RiskMonitor = lazy(() => import('./pages/RiskMonitor'));
const Settings = lazy(() => import('./pages/Settings'));
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const Onboarding = lazy(() => import('./pages/Onboarding'));

// Marketplace pages (lazy loaded)
const Marketplace = lazy(() => import('./pages/marketplace/Marketplace'));
const ListingDetail = lazy(() => import('./pages/marketplace/ListingDetail'));
const FarmerDashboard = lazy(() => import('./pages/farmer/FarmerDashboard'));
const CreateListing = lazy(() => import('./pages/farmer/CreateListing'));
const Conversations = lazy(() => import('./pages/messages/Conversations'));
const Chat = lazy(() => import('./pages/messages/Chat'));

// Service landing pages (lazy loaded)
const MarketplaceLanding = lazy(() => import('./pages/services/MarketplaceLanding'));
const DashboardLanding = lazy(() => import('./pages/services/DashboardLanding'));

// Initialize marketplace data on app load
const initApp = () => {
  initializeMarketplaceData();
};
initApp();

// Protected Route Component (Dashboard - Food Bank)
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = getStorageItem('auth', null);
  const hasCompletedOnboarding = getStorageItem('onboarding_complete', null);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Marketplace Protected Route (any marketplace user)
const MarketplaceProtectedRoute = ({ children }) => {
  if (!isMarketplaceAuthenticated()) {
    return <Navigate to="/login?redirect=marketplace" replace />;
  }

  return children;
};

// Farmer Route Guard
const FarmerRoute = ({ children }) => {
  if (!isMarketplaceAuthenticated()) {
    return <Navigate to="/login?redirect=marketplace" replace />;
  }

  if (!isFarmer()) {
    return <Navigate to="/marketplace" replace />;
  }

  return children;
};

// Restaurant Route Guard
const RestaurantRoute = ({ children }) => {
  if (!isMarketplaceAuthenticated()) {
    return <Navigate to="/login?redirect=marketplace" replace />;
  }

  if (!isRestaurant()) {
    return <Navigate to="/farmer/dashboard" replace />;
  }

  return children;
};

// Layout wrapper to conditionally show Header
const Layout = ({ children }) => {
  const location = useLocation();

  // Routes that should hide all headers
  const hideHeaderRoutes = ['/', '/login', '/signup', '/onboarding', '/services/marketplace', '/services/dashboard'];

  // Routes that should use the marketplace header
  const marketplaceRoutes = [
    '/marketplace',
    '/farmer',
    '/messages'
  ];

  const isMarketplaceRoute = marketplaceRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  const shouldHideHeader = hideHeaderRoutes.includes(location.pathname);

  return (
    <div className="min-h-screen bg-stone-50">
      {!shouldHideHeader && (
        isMarketplaceRoute ? <MarketplaceHeader /> : <Header />
      )}
      {children}
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <Layout>
            <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ===== PUBLIC ROUTES ===== */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<Onboarding />} />

            {/* ===== SERVICE LANDING PAGES ===== */}
            <Route path="/services/marketplace" element={<MarketplaceLanding />} />
            <Route path="/services/dashboard" element={<DashboardLanding />} />

            {/* ===== TRACK 1: DASHBOARD (Food Banks) ===== */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forecasts"
              element={
                <ProtectedRoute>
                  <Forecasts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forecast/:productId"
              element={
                <ProtectedRoute>
                  <ForecastDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forecast-alternative"
              element={
                <ProtectedRoute>
                  <ForecastAlternative />
                </ProtectedRoute>
              }
            />
            <Route
              path="/risks"
              element={
                <ProtectedRoute>
                  <RiskMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* ===== TRACK 2: MARKETPLACE ===== */}

            {/* Restaurant Routes */}
            <Route
              path="/marketplace"
              element={
                <RestaurantRoute>
                  <Marketplace />
                </RestaurantRoute>
              }
            />
            <Route
              path="/marketplace/:id"
              element={
                <RestaurantRoute>
                  <ListingDetail />
                </RestaurantRoute>
              }
            />

            {/* Farmer Routes */}
            <Route
              path="/farmer/dashboard"
              element={
                <FarmerRoute>
                  <FarmerDashboard />
                </FarmerRoute>
              }
            />
            <Route
              path="/farmer/listings/new"
              element={
                <FarmerRoute>
                  <CreateListing />
                </FarmerRoute>
              }
            />
            <Route
              path="/farmer/listings/:id/edit"
              element={
                <FarmerRoute>
                  <CreateListing />
                </FarmerRoute>
              }
            />

            {/* Shared Marketplace Routes (both farmers and restaurants) */}
            <Route
              path="/messages"
              element={
                <MarketplaceProtectedRoute>
                  <Conversations />
                </MarketplaceProtectedRoute>
              }
            />
            <Route
              path="/messages/:id"
              element={
                <MarketplaceProtectedRoute>
                  <Chat />
                </MarketplaceProtectedRoute>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </Suspense>
          </Layout>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
